/**
 * Tracked-action registry — the catalog of named user actions we instrument.
 *
 * This is the analog of the Test Runner's test registry: to start tracking a new
 * action, (1) add an entry here, then (2) call trackAction(KEY, context) at the
 * code site. trackAction (src/lib/activityTracker.ts) fires BOTH Datadog RUM
 * (granular, queryable in RUM Explorer / session replay) AND the first-party
 * /api/frontend-activity ingest (so it shows in the in-app Access & Activity report).
 *
 * The "Tracked Actions" tab in AccessActivityReport lists every entry below joined
 * with its real usage counts from frontend_activity_log. Actions that fire WITHOUT
 * a registry entry still get recorded — they just show up flagged "unregistered" in
 * the catalog, prompting someone to register them here.
 *
 * Keys: lower_snake_case, stable (they're stored in the DB and queried in Datadog).
 */
export interface TrackedActionDef {
  /** Stable event name stored in DB + Datadog. */
  key: string;
  /** Human label for the catalog UI. */
  label: string;
  /** What this action means / when it fires. */
  description: string;
  /** Grouping for the catalog (e.g. "Onboarding", "Members", "Admin"). */
  category: string;
}

export const TRACKED_ACTIONS: TrackedActionDef[] = [
  // ---- Onboarding & setup ----
  {
    key: 'sandbox_provisioned',
    label: 'Sandbox provisioned',
    description: 'An admin provisioned a sandbox for a trial organization.',
    category: 'Onboarding',
  },
  {
    key: 'user_invited',
    label: 'User invited',
    description: 'An admin sent (or queued) a user invitation from User Management.',
    category: 'Onboarding',
  },
  {
    key: 'program_setup_completed',
    label: 'Program setup completed',
    description: 'A CBO program setup wizard was completed.',
    category: 'Onboarding',
  },
  {
    key: 'org_onboarding_completed',
    label: 'Org onboarding completed',
    description: 'An organization finished the onboarding wizard.',
    category: 'Onboarding',
  },

  // ---- Members & data ----
  {
    key: 'member_created',
    label: 'Member created',
    description: 'A new member record was created.',
    category: 'Members',
  },
  {
    key: 'csv_exported',
    label: 'CSV / report exported',
    description: 'A user exported a list or report to CSV/XLSX.',
    category: 'Data',
  },

  // ---- Communications ----
  {
    key: 'campaign_sent',
    label: 'Campaign sent',
    description: 'A bulk communication / campaign was sent.',
    category: 'Communications',
  },

  // ---- Admin / reporting ----
  {
    key: 'access_report_viewed',
    label: 'Access & Activity report opened',
    description: 'An admin opened the Access & Activity report (sandbox / user-mgmt).',
    category: 'Admin',
  },
];

export const TRACKED_ACTION_BY_KEY: Record<string, TrackedActionDef> = Object.fromEntries(
  TRACKED_ACTIONS.map((a) => [a.key, a]),
);
